import React, { useState, useMemo } from 'react';
import { X, Sparkles, User, Eye, Compass, Layers, Maximize2, Search, Filter } from 'lucide-react';
import {
  VISUAL_REPRESENTATIONS,
  getAllVisualRepresentations,
  VisualEntry,
  getVisualsByCategory,
} from '../../services/visualRegistry';
import { CHARACTER_PROFILES } from '../../services/characterDesigns';

export interface VisualCodexModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSelectVisual?: (visual: VisualEntry) => void;
  currentVisualId?: string;
}

const CATEGORIES: VisualEntry['category'][] = [
  'Characters & Crew',
  'Cosmic Voids & Spaces',
  'Megastructures & Stations',
  'Fleets & Ships',
  'Interiors & Wells',
  'Architecture & Transit',
  'Existential Horror & Ledger',
  'Generative Concept Art (150)',
];

const GENERATIVE_CYCLES = [
  { id: 'all', title: 'All 10 Cycles (150 Plates)' },
  { id: '01_geryon_superstructure', title: 'Cycle 01: Superstructure (Plates 1-15)' },
  { id: '02_conduits_and_crawlways', title: 'Cycle 02: High-Voltage Conduits (Plates 16-30)' },
  { id: '03_caesura_dead_band', title: 'Cycle 03: 53-Picosecond Dead-Band (Plates 31-45)' },
  { id: '04_obsidian_cosmic_ledger', title: 'Cycle 04: Obsidian Cosmic Ledger (Plates 46-60)' },
  { id: '05_cryo_cataclysms', title: 'Cycle 05: Cryo Ruptures & Cataclysms (Plates 61-75)' },
  { id: '06_margin_entities', title: 'Cycle 06: Margin Apex Predators (Plates 76-90)' },
  { id: '07_perseus_citadels', title: 'Cycle 07: Perseus Citadels (Plates 91-105)' },
  { id: '08_synaptic_decay', title: 'Cycle 08: Synaptic Decay & Aphasia (Plates 106-120)' },
  { id: '09_zero_tick_heists', title: 'Cycle 09: Zero-Tick Heists & Murders (Plates 121-135)' },
  { id: '10_planck_ignition', title: 'Cycle 10: Planck Ignition Climax (Plates 136-150)' },
];

export const VisualCodexModal: React.FC<VisualCodexModalProps> = ({
  isOpen,
  onClose,
  onSelectVisual,
  currentVisualId,
}) => {
  const [activeCategory, setActiveCategory] = useState<VisualEntry['category']>('Characters & Crew');
  const [selectedCycle, setSelectedCycle] = useState<string>('all');
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [selectedVisual, setSelectedVisual] = useState<VisualEntry>(() => {
    return (
      getAllVisualRepresentations().find((v) => v.id === currentVisualId) ||
      VISUAL_REPRESENTATIONS[0]
    );
  });
  const [isFullscreen, setIsFullscreen] = useState(false);

  if (!isOpen) return null;

  const currentCategoryList = useMemo(() => {
    let list = getVisualsByCategory(activeCategory);

    if (activeCategory === 'Generative Concept Art (150)' && selectedCycle !== 'all') {
      list = list.filter((v) => v.caption.toLowerCase().includes(selectedCycle.replace(/^\d+_/, '').replace(/_/g, ' ')));
    }

    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      list = list.filter(
        (v) =>
          v.title.toLowerCase().includes(q) ||
          v.prompt.toLowerCase().includes(q) ||
          v.caption.toLowerCase().includes(q)
      );
    }
    return list;
  }, [activeCategory, selectedCycle, searchQuery]);

  const totalArchivesCount = getAllVisualRepresentations().length;
  const characterProfile = selectedVisual.characterId
    ? CHARACTER_PROFILES[selectedVisual.characterId]
    : undefined;

  const displayImage = selectedVisual.heroImage || selectedVisual.svgDataUri;

  return (
    <div
      className="caesura-modal-overlay visual-codex-overlay"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-labelledby="visual-codex-title"
    >
      <div
        className="caesura-modal-card visual-codex-card"
        onClick={(e) => e.stopPropagation()}
        style={{
          boxShadow: `0 0 50px ${selectedVisual.accentColor}33`,
          borderColor: selectedVisual.accentColor,
        }}
      >
        {/* Header */}
        <header className="visual-codex-header">
          <div className="codex-header-title-wrap">
            <Layers size={18} style={{ color: selectedVisual.accentColor }} />
            <div>
              <h2 id="visual-codex-title" className="codex-main-title">
                THE CAESURA VISUAL CODEX // {totalArchivesCount} MASTER & GENERATIVE ARCHIVES
              </h2>
              <span className="codex-sub-title">
                150 WIDESCREEN CONCEPT PLATES (1280x720 16:9) & REPEATABLE CHARACTER ROSTER
              </span>
            </div>
          </div>
          <button
            onClick={onClose}
            className="codex-close-btn"
            aria-label="Close Visual Codex"
          >
            <X size={18} />
          </button>
        </header>

        {/* Category Tab Bar */}
        <nav className="codex-category-tabs" aria-label="Visual Categories">
          {CATEGORIES.map((cat) => {
            const count = getVisualsByCategory(cat).length;
            const isActive = activeCategory === cat;
            return (
              <button
                key={cat}
                onClick={() => {
                  setActiveCategory(cat);
                  const firstOfCat = getVisualsByCategory(cat)[0];
                  if (firstOfCat) setSelectedVisual(firstOfCat);
                }}
                className={`codex-tab-btn ${isActive ? 'active-tab' : ''}`}
              >
                <span>{cat.toUpperCase()}</span>
                <span className="tab-count-badge">({count})</span>
              </button>
            );
          })}
        </nav>

        {/* Filter & Search Bar */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '0.8rem',
            padding: '0.6rem 1.6rem',
            background: 'rgba(8, 14, 24, 0.85)',
            borderBottom: '1px solid rgba(255, 255, 255, 0.08)',
            flexWrap: 'wrap',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', flex: 1, minWidth: '180px' }}>
            <Search size={14} style={{ color: '#00f3ff' }} />
            <input
              type="text"
              placeholder="Search visual archives by keyword, plate #, or description..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              style={{
                width: '100%',
                background: '#03060a',
                border: '1px solid rgba(0, 243, 255, 0.25)',
                borderRadius: '3px',
                color: '#e2e8f0',
                padding: '0.35rem 0.6rem',
                fontSize: '0.74rem',
                fontFamily: 'var(--font-mono, monospace)',
                outline: 'none',
              }}
            />
          </div>

          {activeCategory === 'Generative Concept Art (150)' && (
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
              <Filter size={13} style={{ color: '#f59e0b' }} />
              <select
                value={selectedCycle}
                onChange={(e) => setSelectedCycle(e.target.value)}
                style={{
                  background: '#03060a',
                  border: '1px solid rgba(245, 158, 11, 0.35)',
                  borderRadius: '3px',
                  color: '#fbbf24',
                  padding: '0.35rem 0.6rem',
                  fontSize: '0.72rem',
                  fontFamily: 'var(--font-mono, monospace)',
                  outline: 'none',
                  cursor: 'pointer',
                }}
              >
                {GENERATIVE_CYCLES.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.title}
                  </option>
                ))}
              </select>
            </div>
          )}

          <span
            style={{
              fontFamily: 'var(--font-mono, monospace)',
              fontSize: '0.68rem',
              color: '#8da4be',
              marginLeft: 'auto',
            }}
          >
            SHOWING: <strong style={{ color: '#00f3ff' }}>{currentCategoryList.length}</strong> PLATES
          </span>
        </div>

        {/* Main Content: Split Preview & Grid */}
        <div className="codex-body-split">
          {/* Left Hero Inspector */}
          <div className="codex-inspector-pane">
            <div className="inspector-artwork-wrapper">
              <img
                src={displayImage}
                alt={selectedVisual.title}
                className="inspector-art-img"
              />
              <div className="artwork-vignette-mask" />
              <button
                onClick={() => setIsFullscreen(!isFullscreen)}
                className="codex-expand-btn"
                title="Toggle Fullscreen"
              >
                <Maximize2 size={15} />
              </button>
              {selectedVisual.heroImage && (
                <div className="hero-badge">
                  <Sparkles size={11} />
                  <span>MASTER CONCEPT ART</span>
                </div>
              )}
            </div>

            {/* Inspector Meta */}
            <div className="inspector-meta-box">
              <div className="inspector-title-row">
                <h3 style={{ color: selectedVisual.accentColor }}>
                  {selectedVisual.title}
                </h3>
                <span
                  className="inspector-category-tag"
                  style={{ borderColor: selectedVisual.secondaryColor }}
                >
                  {selectedVisual.category.toUpperCase()}
                </span>
              </div>

              <div className="inspector-caption-bar">
                <code>{selectedVisual.caption}</code>
              </div>

              {/* Character Profile Section if Character */}
              {characterProfile && (
                <div className="inspector-character-details">
                  <div className="char-badge-strip">
                    <span className="char-role">
                      <User size={12} /> {characterProfile.role}
                    </span>
                    {characterProfile.age && (
                      <span className="char-age">AGE {characterProfile.age}</span>
                    )}
                  </div>
                  <p className="char-desc-text">
                    <strong>Physical:</strong> {characterProfile.physicalDescription}
                  </p>
                  <p className="char-psych-text">
                    <strong>Psych Eval:</strong> {characterProfile.psychEvaluation}
                  </p>
                </div>
              )}

              <p className="inspector-prompt-text">
                <Compass size={13} style={{ display: 'inline', marginRight: 4 }} />
                <em>Prompt Architecture:</em> {selectedVisual.prompt}
              </p>

              {onSelectVisual && (
                <button
                  onClick={() => {
                    onSelectVisual(selectedVisual);
                    onClose();
                  }}
                  className="codex-apply-btn"
                  style={{
                    backgroundColor: `${selectedVisual.accentColor}22`,
                    borderColor: selectedVisual.accentColor,
                    color: selectedVisual.accentColor,
                  }}
                >
                  <Eye size={14} />
                  <span>APPLY TO CURRENT PAGE ARTWORK</span>
                </button>
              )}
            </div>
          </div>

          {/* Right Thumbnails Grid */}
          <div className="codex-grid-pane">
            <div className="codex-thumbnails-grid">
              {currentCategoryList.map((item) => {
                const isSelected = selectedVisual.id === item.id;
                const thumbSrc = item.heroImage || item.svgDataUri;
                return (
                  <div
                    key={item.id}
                    onClick={() => setSelectedVisual(item)}
                    className={`codex-thumb-card ${isSelected ? 'selected-thumb' : ''}`}
                    style={{
                      borderColor: isSelected ? item.accentColor : 'transparent',
                    }}
                  >
                    <div className="thumb-img-wrapper">
                      <img src={thumbSrc} alt={item.title} loading="lazy" />
                      {item.heroImage && (
                        <span className="thumb-hero-star">★</span>
                      )}
                    </div>
                    <div className="thumb-title-label">{item.title}</div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
